'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { contactMock } from '@/shared/store/mocks/contact.mock';

const SUBMIT_COOLDOWN_MS = 10_000;

const FIELD_CONFIG_MAP = contactMock.form.fields.reduce<Record<string, (typeof contactMock.form.fields)[number]>>(
  (acc, field) => {
    acc[field.name] = field;
    return acc;
  },
  {}
);

export function useContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [cooldownActive, setCooldownActive] = useState(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const validate = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    const nameConfig = FIELD_CONFIG_MAP.name;
    const emailConfig = FIELD_CONFIG_MAP.email;
    const messageConfig = FIELD_CONFIG_MAP.message;

    if (!formData.name.trim() && nameConfig?.errorRequired) {
      nextErrors.name = nameConfig.errorRequired;
    }

    if (!formData.email.trim()) {
      if (emailConfig?.errorRequired) {
        nextErrors.email = emailConfig.errorRequired;
      }
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && emailConfig?.errorInvalid) {
      nextErrors.email = emailConfig.errorInvalid;
    }

    const messageValue = formData.message.trim();
    const minLength = messageConfig?.minLength ?? 10;
    if (!messageValue) {
      if (messageConfig?.errorRequired) {
        nextErrors.message = messageConfig.errorRequired;
      }
    } else if (messageValue.length < minLength && messageConfig?.errorMinLength) {
      nextErrors.message = messageConfig.errorMinLength;
    }

    setFieldErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }, [formData]);

  const handleFieldChange = useCallback((name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const isValid = validate();
      if (!isValid) {
        setStatus('idle');
        setErrorMessage('');
        return;
      }
      const form = e.currentTarget as HTMLFormElement;
      const honeypot = form.elements.namedItem('website') as HTMLInputElement | null;
      if (honeypot?.value) return; // bot trap

      const serviceId = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID;
      const templateId = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        setStatus('error');
        setErrorMessage('EmailJS is not configured. Add NEXT_PUBLIC_EMAILJS_* to .env.local');
        return;
      }

      setStatus('sending');
      setErrorMessage('');

      const logoUrl =
        typeof window !== 'undefined'
          ? `${window.location.origin}/assets/logo/wihe_logo.svg`
          : '';

      try {
        await emailjs.send(
          serviceId,
          templateId,
          {
            from_name: formData.name,
            from_email: formData.email,
            message: formData.message,
            reply_to: formData.email,
            logo_url: logoUrl,
          },
          publicKey
        );
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setCooldownActive(true);
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = setTimeout(() => {
          cooldownTimerRef.current = null;
          setCooldownActive(false);
        }, SUBMIT_COOLDOWN_MS);
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to send message');
      }
    },
    [formData, validate]
  );

  const isSubmitDisabled = status === 'sending' || cooldownActive;

  return { formData, status, errorMessage, fieldErrors, handleFieldChange, handleSubmit, isSubmitDisabled };
}
